import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const EMPTY = {loading: false, overrides: {}, append: []};

const StateContext = createContext(EMPTY);
const ActionsContext = createContext({
  register: () => {},
  unregister: () => {},
});

/**
 * Collects breadcrumb contributions from whatever page is mounted under the
 * layout. Each contributor is keyed by its own id and dropped when it
 * unmounts, so navigating away resets the trail with no explicit cleanup.
 *
 * Split into two contexts so pages that only contribute never re-render when
 * the merged state changes; only the breadcrumb bar subscribes to it.
 */
export function BreadcrumbsProvider({children}) {
  const [entries, setEntries] = useState({});

  const actions = useMemo(
    () => ({
      register: (id, contribution) =>
        setEntries(current => ({...current, [id]: contribution})),
      unregister: id => setEntries(({[id]: removed, ...rest}) => rest),
    }),
    []
  );

  const state = useMemo(
    () =>
      Object.values(entries).reduce(
        (merged, {loading = false, overrides = {}, append = []}) => ({
          loading: merged.loading || loading,
          overrides: {...merged.overrides, ...overrides},
          append: [...merged.append, ...append],
        }),
        EMPTY
      ),
    [entries]
  );

  return (
    <ActionsContext.Provider value={actions}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </ActionsContext.Provider>
  );
}

BreadcrumbsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Contribute to the breadcrumb trail from a page.
 *
 *   // Relabel and relink an ancestor once its data is known. `:id` in
 *   // `/applications/:id/view` is the applicant, so the inferred overview
 *   // link needs the application id instead.
 *   useBreadcrumbs({
 *     loading: isLoading,
 *     overrides: {
 *       application_overview: {
 *         label: 'Application for 1 Foo St',
 *         href: '/applications/42',
 *       },
 *     },
 *   });
 *
 *   // Add crumbs the manifest cannot know about (tabs, wizard steps).
 *   useBreadcrumbs({append: [{label: 'References'}]});
 *
 * `overrides` is keyed by manifest slug and may target the page's own entry
 * or any ancestor; a string is shorthand for `{label}`. `append` adds crumbs
 * after the current page, and the last one becomes the active page.
 *
 * Pass `loading` while the data an override needs is still on its way. The
 * bar shows a skeleton in place of the trail until every contributor has
 * finished, so a crumb appears once with its final text instead of swapping
 * from the manifest label. Falsy override values are ignored either way.
 *
 * @param {{loading?: boolean, overrides?: object, append?: object[]}} contribution
 */
export function useBreadcrumbs({loading, overrides, append} = {}) {
  const id = useId();
  const {register, unregister} = useContext(ActionsContext);
  // Serialised so callers can pass a fresh object literal each render and only
  // re-register when the content actually changes.
  const key = JSON.stringify({loading, overrides, append});

  useEffect(() => {
    register(id, JSON.parse(key));
  }, [id, key, register]);

  useEffect(() => () => unregister(id), [id, unregister]);
}

/**
 * Merged contributions from every mounted page. Consumed by the breadcrumb bar.
 *
 * @returns {{loading: boolean, overrides: object, append: object[]}}
 */
export function useBreadcrumbContributions() {
  return useContext(StateContext);
}
